<script lang="ts">
  // Shop thumbnail for a duel disk. The painted cel places the disk at the avatar's forearm, far
  // off-centre on a wide, mostly-transparent canvas, so a plain <img> looks adrift. We crop to the
  // disk's measured alpha bounding box and draw it CENTRED into a canvas. `drawImage` (display only,
  // never getImageData/toDataURL) doesn't trip the cross-origin taint, so it's safe when the art is
  // served from the R2 bucket in prod.
  import { onMount } from "svelte";
  import type { PortraitManifest, PortraitSlot } from "@ygoevo/yo2-data";
  import { asset, loadWorldData } from "../../../data.ts";

  let { slot, variant, color, gender = "cm" }: { slot: string; variant: string; color?: number; gender?: "cm" | "cw" } = $props();

  // Disk alpha bounding box on its (cm `_r`) cel, [x, y, w, h] in source px, measured from the art.
  // Per-silhouette (colourways share the same silhouette so they share the crop box).
  const BBOX: Record<string, [number, number, number, number]> = {
    dd_01: [399, 84, 288, 209],
    dd_02: [370, 93, 359, 240],
    dd_03: [315, 65, 447, 221],
    da_02: [321, 80, 437, 255],
    da_03: [265, 82, 495, 231],
  };

  let canvas: HTMLCanvasElement;
  async function paint() {
    let src = "";
    try {
      const m: PortraitManifest = (await loadWorldData()).avatarsPortrait;
      const s = m.genders[gender]?.slots.find((x: PortraitSlot) => x.slot === slot);
      const v = s?.variants.find((x) => x.variant === variant);
      // Prefer the explicitly requested colour; otherwise the silhouette's first.
      const byColor = v && (
        (color != null && v.cels[String(color)]) ||
        v.cels[String(v.colors[0])] ||
        Object.values(v.cels)[0]
      );
      const p = byColor && (byColor.r ?? byColor.n);
      if (p) src = asset(p);
    } catch {
      return; // art is cosmetic, a missing thumbnail just shows the frame
    }
    const cx = canvas?.getContext("2d");
    if (!src || !cx) return;
    const img = new Image();
    img.onload = () => {
      const [bx, by, bw, bh] = BBOX[`${slot}_${variant}`] ?? [0, 0, img.naturalWidth, img.naturalHeight];
      const pad = 1.02;
      const Cw = canvas.width, Ch = canvas.height;
      const k = Math.min(Cw / (bw * pad), Ch / (bh * pad));
      const dw = bw * k, dh = bh * k;
      cx.clearRect(0, 0, Cw, Ch);
      cx.imageSmoothingEnabled = true;
      cx.imageSmoothingQuality = "high";
      cx.drawImage(img, bx, by, bw, bh, (Cw - dw) / 2, (Ch - dh) / 2, dw, dh);
    };
    img.src = src;
  }
  // Repaint when slot/variant/color/gender change so the shop swatches and the customizer
  // preview always reflect the current selection.
  $effect(() => { void slot; void variant; void color; void gender; if (canvas) void paint(); });
  onMount(paint);
</script>

<!-- Square canvas so it fills the square tile without 4:3→1:1 stretch; the wide disk is scaled to
     fill the width and centred vertically. -->
<canvas bind:this={canvas} width="320" height="320" class="size-full"></canvas>
